/* eslint camelcase: 0 no-empty:0 */
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import jwtDecode from 'jwt-decode';

import oktaAuth from '../auth/oktaAuth';
import { getAppConfig } from '../utilities/configUtils';

/**
 * Supported log levels
 * @typedef {Array} supportedLogLevels
 * @property {string} debug - Designates fine-grained informational events that are most useful to debug an application.
 * @property {string} info - Designates informational messages that highlight the progress of the application at coarse-grained level.
 * @property {string} warn - Designates potentially harmful situations.
 * @property {string} error - Designates error events that might still allow the application to continue running.
 */
const supportedLogLevels = ['debug', 'info', 'warn', 'error'];

/**
 * Supported log handler
 * @typedef {Array} supportedLogHandler
 * @property {string} http - Send logs to backend serve, preferred configuration for production environment.
 * @property {string} console - Send logs to browser console instead of backend server.
 * @property {string} silent - Turn off the logs completely.
 */
const supportedLogHandler = ['http', 'console', 'silent'];

/**
 * Default logger configuration
 * @typedef {Object} defaultLogConfig
 */
const defaultLogConfig = {
  level: 'error',
  handler: 'http'
};

const getDatadogConfigFromApp = () => {
  const config = getAppConfig('datadogConfig') || undefined;
  return typeof config === 'object' ? config : {};
};

const setLogLevel = () => {
  const config = getAppConfig('log') || undefined;
  const level =
    (typeof config === 'object' && config.level && supportedLogLevels.find(l => l === config.level)) ||
    defaultLogConfig.level;
  datadogLogs.logger.setLevel(level);
};

const setLogHandler = () => {
  const config = getAppConfig('log') || undefined;
  const handler =
    (typeof config === 'object' && config.handler && supportedLogHandler.find(l => l === config.handler)) ||
    defaultLogConfig.handler;
  datadogLogs.logger.setHandler(handler);
};

const setDefaultContext = async () => {
  const { clientId: app_id } = getAppConfig('oktaOIDC');
  let env = getAppConfig('env');
  env = env ? env.toString().toLowerCase() : '';
  datadogLogs.setGlobalContext({
    app_id,
    env,
    date: new Date()
  });
  datadogRum.setGlobalContext({
    app_id,
    env,
    date: new Date()
  });
};

const setGlobalContext = async () => {
  let token = {};
  const jwtToken = await oktaAuth.getAccessToken();
  token = jwtToken ? jwtDecode(jwtToken) : {};

  const userId = token.uid || undefined;
  const sessionId = token.jti || undefined;

  datadogLogs.setGlobalContextProperty('user', userId);
  datadogLogs.setGlobalContextProperty('session_id', sessionId);
  datadogRum.setGlobalContextProperty('user', userId);
  datadogRum.setGlobalContextProperty('session_id', sessionId);
};

const maskEmail = (data = '') => {
  let updatedData = data;
  const EMAIL_REGEX =
    /(([^<>()[\]\\.,/;:\s@"]+(\.[^<>()[\]\\.,;/:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
  const hasEmail = EMAIL_REGEX.test(data);
  if (hasEmail) {
    updatedData = data.replace(EMAIL_REGEX, '***emailId***');
    return maskEmail(updatedData);
  }
  return updatedData;
};

const parseContext = context => {
  let log_context;
  let response_time;
  if (typeof context === 'object') {
    // TODO https://babeljs.io/docs/en/babel-plugin-transform-object-rest-spread
    try {
      log_context = context && context.logContext ? JSON.stringify(context.logContext) : JSON.stringify(context);
    } catch (ignore) {}
    response_time = context && context.responseTime ? context.responseTime : undefined;
  } else {
    log_context = context;
  }
  return { log_context: maskEmail(log_context), response_time };
};

const addLoggerContext = (key, value) => datadogLogs.logger.setContextProperty(key, value);

const logger = {
  debug: (message, context) => datadogLogs.logger.debug(maskEmail(message), parseContext(context)),
  info: (message, context) => datadogLogs.logger.info(maskEmail(message), parseContext(context)),
  warn: (message, context) => datadogLogs.logger.warn(maskEmail(message), parseContext(context)),
  error: (message, context) => datadogLogs.logger.error(maskEmail(message), parseContext(context)),
  addLoggerContext: (key, value) => datadogLogs.logger.setContextProperty(key, value)
};

const datadogConfigFromApp = getDatadogConfigFromApp();

// DATADOG LOGS INIT
datadogLogs.init({
  ...datadogConfigFromApp
});
// DATADOG RUM INIT
datadogRum.init({
  ...datadogConfigFromApp
});

setLogLevel();
setLogHandler();
setDefaultContext();

datadogRum.startSessionReplayRecording();

export { addLoggerContext, logger, setGlobalContext };
